import "./fonts";
import "./i18n";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import { QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router";

import { queryClient } from "@/lib/queryClient";
import { useSettingsStore } from "./stores/settings";

import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import { AuthLayout } from "@/components/layout/AuthLayout";
import PublicLayout from "@/components/layout/PublicLayout";
import { NotificationsToaster } from "@/components/notifications/NotificationsToaster";
import { getTheme } from "@/theme";

const shouldEnableCssLayer = import.meta.env.MODE !== "test";
const cascadeLayerStyles = "@layer theme, base, mui, components, utilities;";

const Collections = lazy(() => import("@/pages/Collections"));
const BrowseLibrary = lazy(() => import("@/pages/BrowseLibrary"));
const Callback = lazy(() => import("@/pages/Callback"));
const Contact = lazy(() => import("@/pages/Contact"));
const EmailList = lazy(() => import("@/pages/EmailList"));
const Home = lazy(() => import("@/pages/Home"));
const Libraries = lazy(() => import("@/pages/Libraries"));
const Login = lazy(() => import("@/pages/Login"));
const Messages = lazy(() => import("@/pages/Messages"));
const OrganisationDetail = lazy(() => import("@/pages/OrganisationDetail"));
const Organisations = lazy(() => import("@/pages/Organisations"));
const PasswordReset = lazy(() => import("@/pages/PasswordReset"));
const PasswordResetKey = lazy(() => import("@/pages/PasswordResetKey"));
const Resources = lazy(() => import("@/pages/Resources"));
const NotFound = lazy(() => import("@/pages/NotFound"));

async function enableMocking() {
    if (!import.meta.env.DEV || import.meta.env.VITE_DJANGO_DEV_URL) {
        return;
    }

    const { worker } = await import("./mocks/browser");

    // `worker.start()` returns a Promise that resolves
    // once the Service Worker is up and ready to intercept requests.
    return worker.start();
}

function PageLoader() {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "100vh",
                gap: 2,
            }}
        >
            <CircularProgress size={40} />
        </Box>
    );
}

export function RootApp() {
    const { theme } = useSettingsStore();
    const preferred: "light" | "dark" =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light";
    const actualMode = theme === "system" ? preferred : theme;
    return (
        <StrictMode>
            <StyledEngineProvider enableCssLayer={shouldEnableCssLayer}>
                {shouldEnableCssLayer ? (
                    <GlobalStyles styles={cascadeLayerStyles} />
                ) : null}
                <ThemeProvider theme={getTheme(actualMode)}>
                    <CssBaseline />
                    <QueryClientProvider client={queryClient}>
                        <BrowserRouter>
                            <Suspense fallback={<PageLoader />}>
                                <Routes>
                                    <Route
                                        path="/login"
                                        element={
                                            <AuthLayout>
                                                <Login />
                                            </AuthLayout>
                                        }
                                    />
                                    <Route
                                        path="/callback"
                                        element={
                                            <PublicLayout>
                                                <Callback />
                                            </PublicLayout>
                                        }
                                    />
                                    <Route
                                        path="/contact"
                                        element={
                                            <AuthLayout>
                                                <Contact />
                                            </AuthLayout>
                                        }
                                    />
                                    <Route
                                        path="/account/password"
                                        element={
                                            <AuthLayout>
                                                <PasswordReset />
                                            </AuthLayout>
                                        }
                                    />
                                    <Route
                                        path="/account/password/reset"
                                        element={
                                            <AuthLayout>
                                                <PasswordResetKey />
                                            </AuthLayout>
                                        }
                                    />
                                    <Route
                                        path="*"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <Suspense
                                                        fallback={
                                                            <PageLoader />
                                                        }
                                                    >
                                                        <Routes>
                                                            <Route
                                                                path="/"
                                                                element={
                                                                    <Home />
                                                                }
                                                            />
                                                            <Route
                                                                path="/browse"
                                                                element={
                                                                    <BrowseLibrary />
                                                                }
                                                            />
                                                            <Route
                                                                path="/browse/:specialtySlug"
                                                                element={
                                                                    <BrowseLibrary />
                                                                }
                                                            />
                                                            <Route
                                                                path="/browse/:specialtySlug/:procedureSlug"
                                                                element={
                                                                    <BrowseLibrary />
                                                                }
                                                            />
                                                            <Route
                                                                path="/resources"
                                                                element={
                                                                    <Resources />
                                                                }
                                                            />
                                                            <Route
                                                                path="/email"
                                                                element={
                                                                    <EmailList />
                                                                }
                                                            />
                                                            <Route
                                                                path="/messages"
                                                                element={
                                                                    <Messages />
                                                                }
                                                            />
                                                            <Route
                                                                path="/organisations"
                                                                element={
                                                                    <Organisations />
                                                                }
                                                            />
                                                            <Route
                                                                path="/organisations/:id"
                                                                element={
                                                                    <OrganisationDetail />
                                                                }
                                                            />
                                                            <Route
                                                                path="/organisations/:id/:childId"
                                                                element={
                                                                    <OrganisationDetail />
                                                                }
                                                            />
                                                            <Route
                                                                path="/libraries"
                                                                element={
                                                                    <Libraries />
                                                                }
                                                            />
                                                            <Route
                                                                path="/collections"
                                                                element={
                                                                    <Collections />
                                                                }
                                                            />
                                                            <Route
                                                                path="/password-reset"
                                                                element={
                                                                    <PasswordReset />
                                                                }
                                                            />
                                                            <Route
                                                                path="/password-reset/:key"
                                                                element={
                                                                    <PasswordResetKey />
                                                                }
                                                            />
                                                            <Route
                                                                path="*"
                                                                element={
                                                                    <NotFound />
                                                                }
                                                            />
                                                        </Routes>
                                                    </Suspense>
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                </Routes>
                            </Suspense>
                            <NotificationsToaster />
                        </BrowserRouter>
                    </QueryClientProvider>
                </ThemeProvider>
            </StyledEngineProvider>
        </StrictMode>
    );
}

enableMocking().then(() => {
    const container = document.getElementById("root")!;
    // Prevent creating more than one root during HMR
    interface RootContainer {
        __root?: ReturnType<typeof createRoot>;
    }

    const anyContainer = container as unknown as RootContainer;
    if (anyContainer.__root) {
        anyContainer.__root.render(<RootApp />);
    } else {
        const root = createRoot(container);
        anyContainer.__root = root;
        root.render(<RootApp />);
    }
});
